import React from 'react';
import { Card, Image, Box, IconButton, Icon } from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  HeartIcon,
  DocumentDuplicateIcon,
  Bars3Icon,
  ShareIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { useParams } from 'react-router-dom';
import { Tooltip } from '@/components/ui/tooltip';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Content, Favorite } from '@/lib/types';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';

type Props = {
  item: Content;
  favorites: { [key: number]: Favorite };
  viewMode: 'list' | 'grid';
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  onView: (item: Content) => void;
  onFavourite: (item: Content) => void;
  onGeneratePiblicLink: (item: Content) => void;
  onDuplicate: (item: Content) => void;
  onEdit: (item: Content) => void;
  onDelete: (item: Content) => void;
  onChangeStatus: (item: Content, status: 'draft' | 'published') => void;
};

const ItemCard: React.FC<Props> = ({
  item,
  favorites,
  viewMode,
  lastContentElementRef,
  onView,
  onFavourite,
  onGeneratePiblicLink,
  onDuplicate,
  onEdit,
  onDelete,
  onChangeStatus,
}) => {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const isAdmin = useIsInAdminGroup();
  const isListView = viewMode === 'list';
  const imageWidth = isListView ? '50px' : '100%';
  const imageHeight = isListView ? '50px' : '130px';
  const imageSrc = item.thumbnail ? item.thumbnail : '/no_media.png';

  function isFavourite(item: Content) {
    return favorites[item.id] ? true : false;
  }

  function actionPermission(itemWorkspaceId: number) {
    return isAdmin && workspaceId && itemWorkspaceId === parseInt(workspaceId, 10);
  }

  return (
    <Card.Root
      key={item.reference_id}
      ref={lastContentElementRef}
      borderWidth={viewMode === 'list' ? 0 : 1}
    >
      <Box display={viewMode === 'list' ? 'flex' : 'block'} alignItems={'center'}>
        <Image
          objectFit={'cover'}
          w={imageWidth}
          h={imageHeight}
          src={imageSrc}
          borderRadius={'md'}
          alt={item.name}
          loading='lazy'
          _hover={{ cursor: 'pointer' }}
          onClick={() => onView(item)}
        />
        <Card.Body p={2} _hover={{ cursor: 'pointer' }} onClick={() => onView(item)}>
          <Card.Title fontSize={'sm'} lineHeight={1.5}>
            {item.name}
          </Card.Title>
          <Card.Description mt={1} color={'fg.muted'} fontSize={'xs'}>
            {new Date(item.created_at).toLocaleDateString()}
            {item.created_user && ` by ${item.created_user}`}
            {item.workspace && ` @${item.workspace}`}
          </Card.Description>
        </Card.Body>
        <Card.Footer px={2} pb={2}>
          <Box display={viewMode === 'list' ? { base: 'none', md: 'flex' } : ''}>
            <Tooltip content={'Favourite'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Favourite'}
                size={'sm'}
                onClick={() => onFavourite(item)}
              >
                <Icon
                  as={isFavourite(item) ? HeartSolidIcon : HeartIcon}
                  color={isFavourite(item) ? 'red.600' : 'gray.700'}
                />
              </IconButton>
            </Tooltip>
            {item.public_link && !actionPermission(item.workspace_id) && (
              <Tooltip content={'Share public link'}>
                <IconButton
                  variant={'ghost'}
                  aria-label={'Share public link'}
                  size={'sm'}
                  onClick={() => onGeneratePiblicLink(item)}
                >
                  <Icon as={ShareIcon} />
                </IconButton>
              </Tooltip>
            )}
            {actionPermission(item.workspace_id) && (
              <>
                <Tooltip
                  content={`${item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}`}
                >
                  <IconButton
                    variant={'ghost'}
                    aria-label={`${item.status}`}
                    size={'sm'}
                    onClick={() =>
                      onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                    }
                  >
                    <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Share public link'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Share public link'}
                    size={'sm'}
                    onClick={() => onGeneratePiblicLink(item)}
                  >
                    <Icon as={ShareIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Duplicate'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Duplicate'}
                    size={'sm'}
                    onClick={() => onDuplicate(item)}
                  >
                    <Icon as={DocumentDuplicateIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Edit'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Edit'}
                    size={'sm'}
                    onClick={() => onEdit(item)}
                  >
                    <Icon as={PencilIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Delete'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Delete'}
                    size={'sm'}
                    onClick={() => onDelete(item)}
                  >
                    <Icon as={TrashIcon} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          {viewMode === 'list' && (
            <Box display={{ base: 'block', md: 'none' }}>
              <MenuRoot>
                <MenuTrigger asChild>
                  <IconButton variant='outline' aria-label='Menu' size='sm'>
                    <Icon as={Bars3Icon} />
                  </IconButton>
                </MenuTrigger>
                <MenuContent>
                  <MenuItem value={'favourite'} onClick={() => onFavourite(item)}>
                    <Icon
                      as={isFavourite(item) ? HeartSolidIcon : HeartIcon}
                      color={isFavourite(item) ? 'red.600' : 'gray.700'}
                      w={4}
                      h={4}
                    />
                    Favourite
                  </MenuItem>
                  {item.public_link && !actionPermission(item.workspace_id) && (
                    <MenuItem value='share-public-link' onClick={() => onGeneratePiblicLink(item)}>
                      <Icon as={ShareIcon} w={4} h={4} />
                      Share public link
                    </MenuItem>
                  )}
                  {actionPermission(item.workspace_id) && (
                    <>
                      <MenuItem
                        value={'change-status'}
                        onClick={() =>
                          onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                        }
                      >
                        <Icon
                          as={item.status === 'published' ? EyeIcon : EyeSlashIcon}
                          w={4}
                          h={4}
                        />
                        {item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}
                      </MenuItem>
                      <MenuItem
                        value='share-public-link'
                        onClick={() => onGeneratePiblicLink(item)}
                      >
                        <Icon as={ShareIcon} w={4} h={4} />
                        Share public link
                      </MenuItem>
                      <MenuItem value='duplicate' onClick={() => onDuplicate(item)}>
                        <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                        Duplicate
                      </MenuItem>
                      <MenuItem value='edit' onClick={() => onEdit(item)}>
                        <Icon as={PencilIcon} w={4} h={4} />
                        Edit
                      </MenuItem>
                      <MenuItem value='delete' onClick={() => onDelete(item)}>
                        <Icon as={TrashIcon} w={4} h={4} />
                        Delete
                      </MenuItem>
                    </>
                  )}
                </MenuContent>
              </MenuRoot>
            </Box>
          )}
        </Card.Footer>
      </Box>
    </Card.Root>
  );
};

export default ItemCard;
